import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { TrendingUp, Brain, Shield, BarChart3, Activity, Star, Zap, ArrowRight, BookOpen, Clock, ChevronDown } from 'lucide-react';
import blogPosts from './blogData';

const Landing = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setAuthUser(u);
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is TradeFix AI?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'TradeFix AI is an AI-powered trading performance analyzer built for Indian traders. It helps you identify emotional mistakes, track trading psychology, manage risk, and get personalized AI coaching on every trade — all in one platform.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is TradeFix AI free to use?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, TradeFix AI offers a free Basic plan that lets you log up to 10 trades per month and access 3 AI Coach chats per day. For unlimited trades, advanced analytics, and full AI coaching, you can upgrade to the Pro (₹999/mo) or Elite (₹1999/mo) plan.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does TradeFix AI help Indian traders?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'TradeFix AI is designed specifically for Indian market traders — NSE, BSE, F&O, intraday, and swing traders. It analyzes your trades to surface behavioral patterns like revenge trading, fear-based early exits, and overtrading, then provides actionable AI coaching in real time to help you trade more consistently.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the difference between Pro and Elite plan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Both Pro (₹999/mo) and Elite (₹1999/mo) include unlimited trades, AI Coach insights, Psychology Tracker, Risk Manager, daily streak tracking, monthly progress reports, and per-trade AI insights. Elite additionally offers 2x faster AI responses, advanced analytics, Excel export, and dedicated priority support.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I get started with TradeFix AI?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Getting started is free and takes less than a minute. Click "Start Fixing My Trades", create your account with an email and password, and begin logging your first trade. The AI Coach will immediately start analyzing your patterns. No credit card required for the free plan.',
          },
        },
      ],
    };

    let scriptEl = document.getElementById('faq-schema');
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.id = 'faq-schema';
      scriptEl.type = 'application/ld+json';
      document.head.appendChild(scriptEl);
    }
    scriptEl.textContent = JSON.stringify(faqSchema);

    return () => {
      const el = document.getElementById('faq-schema');
      if (el) el.remove();
    };
  }, []);

  const features = [
    {
      icon: <Brain size={32} className="text-blue-400" />,
      title: 'AI Coach',
      desc: 'Get personalized AI-driven feedback on every trade. Identifies FOMO entries, revenge trades, and emotional patterns — then tells you exactly how to fix them.',
      badge: 'Powered by Groq AI',
    },
    {
      icon: <BarChart3 size={32} className="text-green-400" />,
      title: 'Trade Journal',
      desc: 'Log every trade with entry, exit, P&L, emotion, and setup type. Your complete trading history in one place — searchable, filterable, insightful.',
      badge: 'Smart Journaling',
    },
    {
      icon: <Activity size={32} className="text-purple-400" />,
      title: 'Weekly Reports',
      desc: 'Every week, get a full breakdown of your performance — win rate, best setups, worst patterns, and a personal AI improvement plan.',
      badge: 'Auto-Generated',
    },
    {
      icon: <Shield size={32} className="text-red-400" />,
      title: 'Mistake Tracker',
      desc: 'Automatically tags every trade with mistake types — FOMO, revenge trade, SL removal. See your top 3 mistakes and track how fast you fix them.',
      badge: 'Behavior Analytics',
    },
  ];


  return (
    <div className="landing-page min-h-screen bg-gray-950 text-white font-sans">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-gray-950/95 backdrop-blur border-b border-gray-800 shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-green-400 rounded-lg flex items-center justify-center">
              <TrendingUp size={18} className="text-white" />
            </div>
            <span className="text-xl font-bold text-white">TradeFix AI</span>
          </div>

          {/* Nav Actions */}
          <div className="flex items-center gap-4">
            <a
              href="/blog"
              className="hidden sm:block text-sm font-medium text-gray-300 hover:text-white transition-colors"
            >
              Blog
            </a>
            {!authLoading && (
              authUser ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                >
                  Go to Dashboard →
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate('/signup')}
                    className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                  >
                    Start Free
                  </button>
                </>
              )
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero pt-20 pb-4 sm:pt-32 sm:pb-24 px-4 relative overflow-hidden">

        <div className="max-w-4xl mx-auto text-center relative">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-6">
            <Zap size={14} />
            Built for Indian Traders 🇮🇳
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
            <span className="text-white">90% Indian Traders Lose</span>
            <br />
            <span className="text-white">Because of </span>
            <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
              Psychology
            </span>
            <span className="text-white"> — Not Strategy</span>
          </h1>

          {/* Subtext */}
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            TradeFix AI analyzes every trade, finds your emotional patterns (FOMO, revenge trading, overtrading), and gives you a personal AI coach to fix them. <span className="text-white font-medium">Free to start.</span>
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/signup')}
              className="w-full sm:w-auto px-10 py-5 text-lg font-bold bg-gradient-to-r from-green-500 to-emerald-400 hover:from-green-400 hover:to-emerald-300 text-gray-900 rounded-xl shadow-xl shadow-green-500/30 transition-all flex items-center justify-center gap-2 animate-pulse-subtle"
            >
              Free Mein Shuru Karo
              <ArrowRight size={20} />
            </button>
            <button
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto px-8 py-4 text-base font-semibold bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-xl border border-gray-700 transition-all"
            >
              See How It Works
            </button>
          </div>

          {/* Urgency + social proof */}
          <div className="mt-5 flex flex-col items-center gap-2">
            <p className="text-amber-400 text-sm font-semibold tracking-wide">⚡ Founding price — limited time only</p>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-gray-500 mt-1">
              <div className="flex items-center gap-1.5">
                <span className="text-green-400 font-semibold">👥 500+ traders</span>
                <span>already using TradeFix AI</span>
              </div>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
                ))}
                <span className="ml-1">4.9/5</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-green-400">✓</span>
                <span>14-day money-back guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="landing-problem pt-6 sm:pt-24 pb-24 px-4 bg-gray-950 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          
        </div>

        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/10 border border-red-500/30 rounded-full text-red-400 text-sm font-medium mb-5">
              ⚠️ Sound familiar?
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why most Indian traders{' '}
              <span className="text-red-400">keep losing money</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              It's not lack of knowledge. It's not bad strategies. It's the same 3 psychological traps — over and over.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Problem 1 */}
            <div className="bg-gray-900 border border-red-900/50 rounded-2xl p-7 relative overflow-hidden group hover:border-red-700/60 transition-all">
              <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/5 rounded-full -translate-y-8 translate-x-8" />
              <div className="text-4xl mb-4">😱</div>
              <div className="inline-block px-2 py-0.5 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs font-semibold mb-3 uppercase tracking-wider">
                Problem #1
              </div>
              <h3 className="text-xl font-bold text-white mb-3">FOMO Entry</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-5">
                Stock already moved 5%. Telegram group is screaming "BUY". You jump in at the top — and watch it reverse immediately. Every time.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <span>FOMO trades have 3× higher loss rate than planned trades</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <span>Average entry is 4–6% after the ideal entry point</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <span>"Ye trade miss ho gaya, agla zaroor lunga"</span>
                </div>
              </div>
            </div>

            {/* Problem 2 */}
            <div className="bg-gray-900 border border-red-900/50 rounded-2xl p-7 relative overflow-hidden group hover:border-red-700/60 transition-all">
              <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/5 rounded-full -translate-y-8 translate-x-8" />
              <div className="text-4xl mb-4">🔁</div>
              <div className="inline-block px-2 py-0.5 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs font-semibold mb-3 uppercase tracking-wider">
                Problem #2
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Revenge Trading</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-5">
                One loss becomes two, two becomes five. You keep trading to "recover" — increasing size, breaking rules, and spiraling into your worst days.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <span>Revenge trades cause 2–4× larger losses than normal</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <span>Most traders wipe a week's profits in one bad session</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <span>"Main ek aur trade leta hoon, sab recover ho jayega"</span>
                </div>
              </div>
            </div>

            {/* Problem 3 */}
            <div className="bg-gray-900 border border-red-900/50 rounded-2xl p-7 relative overflow-hidden group hover:border-red-700/60 transition-all">
              <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/5 rounded-full -translate-y-8 translate-x-8" />
              <div className="text-4xl mb-4">🚫</div>
              <div className="inline-block px-2 py-0.5 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs font-semibold mb-3 uppercase tracking-wider">
                Problem #3
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Removing Stop Loss</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-5">
                Trade hits stop loss. Instead of exiting, you remove it. "Ek baar recover ho jayega." It doesn't. Small loss becomes account-wiping loss.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <span>#1 cause of large single-day losses for retail traders</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <span>Average SL-removal loss is 8× the original planned loss</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <span>"Abhi nahi, thoda aur wait karte hain"</span>
                </div>
              </div>
            </div>

            {/* Problem 4 */}
            <div className="bg-gray-900 border border-red-900/50 rounded-2xl p-7 relative overflow-hidden group hover:border-red-700/60 transition-all">
              <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/5 rounded-full -translate-y-8 translate-x-8" />
              <div className="text-4xl mb-4">📱</div>
              <div className="inline-block px-2 py-0.5 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs font-semibold mb-3 uppercase tracking-wider">
                Problem #4
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Overtrading</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-5">
                You trade 20 times when 3 good setups were enough. Each bad trade eats your good profits — and brokerage devours what's left.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <span>Overtraders pay 5–10% of capital in brokerage per month</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <span>78% of retail losses happen after the 5th trade of the day</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <span>"Bas ek aur — aaj profit mein nahi jaana chahta"</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-gray-500 text-sm">
              These aren't character flaws — they're <span className="text-gray-300 font-medium">learnable patterns</span> that can be identified, tracked, and fixed with the right tool.
            </p>
          </div>
        </div>
      </section>

      {/* Mid-page CTA Banner */}
      <section className="py-10 px-4 bg-gradient-to-r from-green-900/30 via-gray-900 to-blue-900/30 border-y border-green-800/30">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-bold text-xl mb-1">Apni psychology fix karo — free mein</p>
            <p className="text-gray-400 text-sm">👥 500+ Indian traders already tracking their mistakes with TradeFix AI</p>
          </div>
          <div className="flex flex-col items-center sm:items-end gap-1 flex-shrink-0">
            <button
              onClick={() => navigate('/signup')}
              className="px-8 py-4 text-base font-bold bg-gradient-to-r from-green-500 to-emerald-400 hover:from-green-400 hover:to-emerald-300 text-gray-900 rounded-xl shadow-lg shadow-green-500/25 transition-all flex items-center gap-2 whitespace-nowrap"
            >
              Free Mein Shuru Karo
              <ArrowRight size={18} />
            </button>
            <p className="text-amber-400 text-xs font-medium">⚡ Founding price — limited time</p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="landing-solution py-24 px-4 bg-gray-900/40 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          
        </div>

        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/10 border border-green-500/30 rounded-full text-green-400 text-sm font-medium mb-5">
              ✅ Here's the fix
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              TradeFix AI turns your{' '}
              <span className="text-green-400">mistakes into a system</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Every problem above has a direct solution built into TradeFix AI. Here's exactly how it works.
            </p>
          </div>

          <div className="space-y-6">
            {/* Solution 1 */}
            <div className="bg-gray-900 border border-green-900/40 rounded-2xl p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-8 items-center hover:border-green-800/60 transition-all">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center">
                    <Brain size={20} className="text-green-400" />
                  </div>
                  <span className="text-green-400 text-sm font-semibold uppercase tracking-wider">Fixes Emotional Trading</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">AI Coach — your personal trading mentor</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  After every trade, the AI Coach analyzes what you did, why it was emotional or disciplined, and gives you specific, personalized feedback. No more guessing what went wrong.
                </p>
              </div>
              <div className="space-y-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Before vs After</div>
                <div className="flex gap-3">
                  <div className="flex-1 bg-red-950/40 border border-red-900/40 rounded-xl p-4">
                    <div className="text-red-400 text-xs font-semibold mb-2">❌ Before</div>
                    <p className="text-gray-400 text-xs leading-relaxed">"I just felt it would go up. It didn't. I don't know why I keep doing this."</p>
                  </div>
                  <div className="flex-1 bg-green-950/40 border border-green-900/40 rounded-xl p-4">
                    <div className="text-green-400 text-xs font-semibold mb-2">✅ After</div>
                    <p className="text-gray-400 text-xs leading-relaxed">"AI flagged: FOMO entry after missing previous move. Pattern found in 6 of last 10 losses."</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Solution 2 */}
            <div className="bg-gray-900 border border-green-900/40 rounded-2xl p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-8 items-center hover:border-green-800/60 transition-all">
              <div className="md:order-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center">
                    <Activity size={20} className="text-green-400" />
                  </div>
                  <span className="text-green-400 text-sm font-semibold uppercase tracking-wider">Stops Revenge Trading</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Psychology Tracker — your emotional dashboard</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Log your emotional state with each trade. The tracker surfaces patterns like "You lose 3× more after a big red day" — so you can catch yourself before the spiral starts.
                </p>
              </div>
              <div className="md:order-1 space-y-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Before vs After</div>
                <div className="flex gap-3">
                  <div className="flex-1 bg-red-950/40 border border-red-900/40 rounded-xl p-4">
                    <div className="text-red-400 text-xs font-semibold mb-2">❌ Before</div>
                    <p className="text-gray-400 text-xs leading-relaxed">"I had a bad Monday. By Friday I'd lost ₹40,000 trying to recover. I didn't even notice it happening."</p>
                  </div>
                  <div className="flex-1 bg-green-950/40 border border-green-900/40 rounded-xl p-4">
                    <div className="text-green-400 text-xs font-semibold mb-2">✅ After</div>
                    <p className="text-gray-400 text-xs leading-relaxed">"Psychology score dropped below 40 after loss — system warned me. Stepped away. Saved the week."</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Solution 3 */}
            <div className="bg-gray-900 border border-green-900/40 rounded-2xl p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-8 items-center hover:border-green-800/60 transition-all">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center">
                    <Shield size={20} className="text-green-400" />
                  </div>
                  <span className="text-green-400 text-sm font-semibold uppercase tracking-wider">Kills FOMO &amp; Overtrading</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Risk Manager + Trade Journal — your trading rules enforcer</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Set daily loss limits and max trades per day. Log every trade with setup type. The AI identifies which setups actually make you money — and which ones you're just taking out of boredom or FOMO.
                </p>
              </div>
              <div className="space-y-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Before vs After</div>
                <div className="flex gap-3">
                  <div className="flex-1 bg-red-950/40 border border-red-900/40 rounded-xl p-4">
                    <div className="text-red-400 text-xs font-semibold mb-2">❌ Before</div>
                    <p className="text-gray-400 text-xs leading-relaxed">"Took 18 trades today. Profitable on 10, but brokerage ate everything. Net: -₹2,100."</p>
                  </div>
                  <div className="flex-1 bg-green-950/40 border border-green-900/40 rounded-xl p-4">
                    <div className="text-green-400 text-xs font-semibold mb-2">✅ After</div>
                    <p className="text-gray-400 text-xs leading-relaxed">"Took 4 trades. Hit daily limit, stopped. Net: +₹3,800. Best Thursday in 3 months."</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-4 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          
        </div>
        <div className="max-w-4xl mx-auto relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-5">
              ⚡ Simple 3-Step Process
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Start improving in <span className="text-blue-400">60 seconds</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              No complex setup. No learning curve. Just log, analyze, and improve.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {/* Connector line (desktop only) */}
            <div className="hidden md:block absolute top-12 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-blue-500/50 to-green-500/50" />

            {/* Step 1 */}
            <div className="relative bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center hover:border-blue-700/50 transition-all">
              <div className="w-16 h-16 bg-blue-500/10 border-2 border-blue-500/40 rounded-full flex items-center justify-center mx-auto mb-5 relative z-10">
                <span className="text-2xl font-extrabold text-blue-400">1</span>
              </div>
              <div className="text-3xl mb-3">📝</div>
              <h3 className="text-xl font-bold text-white mb-3">Add Your Trades</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Log trades in seconds — entry, exit, P&amp;L, emotion, and setup type. Takes less time than placing the order.
              </p>
            </div>

            {/* Step 2 */}
            <div className="relative bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center hover:border-blue-700/50 transition-all md:translate-y-4">
              <div className="w-16 h-16 bg-purple-500/10 border-2 border-purple-500/40 rounded-full flex items-center justify-center mx-auto mb-5 relative z-10">
                <span className="text-2xl font-extrabold text-purple-400">2</span>
              </div>
              <div className="text-3xl mb-3">🤖</div>
              <h3 className="text-xl font-bold text-white mb-3">AI Finds Your Patterns</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                The AI analyzes every trade and surfaces hidden patterns — "You lose 3× more on Mondays after a bad Friday."
              </p>
            </div>

            {/* Step 3 */}
            <div className="relative bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center hover:border-green-700/50 transition-all">
              <div className="w-16 h-16 bg-green-500/10 border-2 border-green-500/40 rounded-full flex items-center justify-center mx-auto mb-5 relative z-10">
                <span className="text-2xl font-extrabold text-green-400">3</span>
              </div>
              <div className="text-3xl mb-3">📈</div>
              <h3 className="text-xl font-bold text-white mb-3">Fix Your Mistakes</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Get personalized AI coaching after each trade. Track your improvement weekly. Watch your win rate climb.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => navigate('/signup')}
              className="w-full sm:w-auto px-8 py-4 text-base font-semibold bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-xl shadow-lg shadow-blue-500/25 transition-all flex items-center justify-center gap-2 mx-auto"
            >
              Free Analysis Shuru Karo →
            </button>
            <p className="text-gray-500 text-sm mt-3">Free plan · Setup in 60 seconds</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 bg-gray-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything you need to <span className="text-blue-400">trade better</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Four powerful tools working together to make you a consistently profitable trader.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <div
                key={i}
                className="group bg-gray-900 border border-gray-800 hover:border-gray-600 rounded-2xl p-6 transition-all hover:shadow-xl hover:shadow-black/30 hover:-translate-y-1 flex gap-5"
              >
                <div className="flex-shrink-0 w-14 h-14 bg-gray-800 rounded-xl flex items-center justify-center">{f.icon}</div>
                <div>
                  <div className="inline-block px-2 py-0.5 bg-gray-800 rounded text-xs text-gray-400 font-medium mb-2">
                    {f.badge}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">{f.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 px-4 bg-gray-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/10 border border-yellow-500/30 rounded-full text-yellow-400 text-sm font-medium mb-5">
              ⭐ Real Traders, Real Results
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What Traders Say About <span className="text-blue-400">TradeFix AI</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Hear from traders across India who've fixed their trading psychology
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: 'Rahul Sharma',
                city: 'Mumbai',
                role: 'Intraday Trader · 3 yrs exp',
                text: '"TradeFix AI ne meri revenge trading completely band karwa di. AI coach ne mujhe bataya ki main Monday mornings me sabse zyada lose karta tha. Ab mera monthly P&L 40% better hai."',
                rating: 5,
                color: 'bg-blue-600',
                border: 'border-blue-500',
                initials: 'RS',
                date: 'January 2026',
              },
              {
                name: 'Priya Patel',
                city: 'Ahmedabad',
                role: 'F&O Trader',
                text: '"Psychology tracker is a game changer. Pehle mujhe pata hi nahi tha ki fear ki wajah se main early exit karta tha. Ab mere winning trades 60% longer hold hote hain."',
                rating: 5,
                color: 'bg-purple-600',
                border: 'border-purple-500',
                initials: 'PP',
                date: 'February 2026',
              },
              {
                name: 'Vikram Singh',
                city: 'Delhi',
                role: 'Swing Trader',
                text: '"Risk Manager feature ne mera account bachaya. Ek din mujhe stop kiya jab main 3 consecutive losses ke baad 4th trade lene wala tha. Best ₹999 I ever spent."',
                rating: 5,
                color: 'bg-green-600',
                border: 'border-green-500',
                initials: 'VS',
                date: 'March 2026',
              },
              {
                name: 'Amit Joshi',
                city: 'Pune',
                role: 'Bank Nifty Options Trader',
                text: '"AI chatbot se trade analysis karna bahut helpful hai. Har trade ke baad detailed feedback milta hai — kahan galti hui aur kaise improve karu. Highly recommended!"',
                rating: 4,
                color: 'bg-orange-600',
                border: 'border-orange-500',
                initials: 'AJ',
                date: 'January 2026',
              },
              {
                name: 'Sneha Reddy',
                city: 'Hyderabad',
                role: 'Beginner Trader · 1 yr',
                text: '"Nayi trader hoon but TradeFix AI ne mujhe sahi habits se shuru karwaya. Dashboard me apne patterns dekhna motivating hai. Best platform for beginners!"',
                rating: 5,
                color: 'bg-pink-600',
                border: 'border-pink-500',
                initials: 'SR',
                date: 'February 2026',
              },
              {
                name: 'Karan Mehta',
                city: 'Bangalore',
                role: 'Full-time Trader',
                text: '"Maine 4 trading journals try kiye — TradeFix AI sabse alag hai. Psychology focus aur AI coaching ki wajah se mera win rate 52% se 71% ho gaya 2 months me."',
                rating: 5,
                color: 'bg-teal-600',
                border: 'border-teal-500',
                initials: 'KM',
                date: 'March 2026',
              },
            ].map((t, i) => (
              <div
                key={i}
                className="bg-gray-900 border border-gray-800 rounded-2xl p-6 flex flex-col gap-4 hover:border-gray-700 transition-all"
              >
                {/* Stars */}
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star
                      key={s}
                      size={15}
                      className={s < t.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-700 fill-gray-700'}
                    />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-gray-300 text-sm leading-relaxed flex-1">{t.text}</p>

                {/* Author + via tag */}
                <div className="pt-2 border-t border-gray-800">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-12 h-12 ${t.color} border-2 ${t.border} rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                      {t.initials}
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">{t.name}</p>
                      <p className="text-gray-500 text-xs">{t.role} · {t.city}</p>
                      <span className="inline-flex items-center gap-1 text-green-400 text-xs font-medium mt-0.5">
                        ✓ Verified User
                      </span>
                      <p className="text-gray-600 text-xs mt-0.5">{t.date}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-xs text-right">via TradeFix AI App</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog CTA */}
      <section className="landing-blog-cta py-16 px-4">
        <div className="max-w-xl mx-auto text-center">
          <p className="text-gray-400 text-lg mb-6">
            Learn trading psychology, improve your mindset, and trade smarter.
          </p>
          <a
            href="/blog"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold rounded-xl shadow-lg transition-all"
          >
            Read Our Blog
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* Blog Section */}
      {(() => {
        const latestPosts = [...blogPosts]
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 3);
        return (
          <section className="landing-blog py-24 px-4 bg-gray-900/50">
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="text-center mb-14">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-5">
                  <BookOpen size={14} />
                  Trading Psychology Tips
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Latest Trading <span className="text-blue-400">Psychology Tips</span>
                </h2>
                <p className="text-gray-400 text-lg max-w-xl mx-auto">
                  Free resources to improve your trading
                </p>
              </div>

              {/* Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {latestPosts.map((post) => (
                  <div
                    key={post.id}
                    className="group bg-gray-900 border border-gray-800 hover:border-blue-700/50 rounded-2xl p-6 flex flex-col transition-all hover:shadow-xl hover:shadow-black/30 hover:-translate-y-1"
                  >
                    {/* Category + read time */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="inline-block px-2.5 py-1 bg-blue-500/10 border border-blue-500/20 rounded-lg text-blue-400 text-xs font-semibold">
                        {post.category}
                      </span>
                      <div className="flex items-center gap-1 text-gray-500 text-xs">
                        <Clock size={11} />
                        {post.readTime}
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-white font-bold text-lg leading-snug mb-3 group-hover:text-blue-300 transition-colors line-clamp-2">
                      {post.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-gray-400 text-sm leading-relaxed flex-1 line-clamp-2 mb-5">
                      {post.excerpt}
                    </p>

                    {/* Read More */}
                    <a
                      href={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-sm font-semibold transition-colors group/link"
                    >
                      Read More
                      <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
                    </a>
                  </div>
                ))}
              </div>

              {/* View All */}
              <div className="mt-10 text-center">
                <a
                  href="/blog"
                  className="inline-flex items-center gap-2 px-7 py-3 bg-gray-800 hover:bg-gray-700 text-white font-semibold rounded-xl border border-gray-700 hover:border-gray-600 transition-all"
                >
                  View All Blogs
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Final CTA Banner */}
      <section className="landing-final-cta py-20 px-4">
        <div className="max-w-3xl mx-auto text-center bg-gradient-to-br from-blue-900/50 via-gray-900 to-green-900/40 border border-blue-700/40 rounded-3xl p-10 sm:p-14 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            
          </div>
          <div className="relative">
            <div className="text-4xl mb-4">🚀</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Apni trading fix karo — <span className="text-green-400">aaj se</span>
            </h2>
            <p className="text-gray-400 text-lg mb-1">
              Join 500+ Indian traders using TradeFix AI to beat their psychology.
            </p>
            <p className="text-amber-400 text-sm font-semibold mb-7">⚡ Founding price — limited time only</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate('/signup')}
                className="w-full sm:w-auto px-10 py-5 text-lg font-bold bg-gradient-to-r from-green-500 to-emerald-400 hover:from-green-400 hover:to-emerald-300 text-gray-900 rounded-xl shadow-xl shadow-green-500/30 transition-all flex items-center justify-center gap-2"
              >
                Free Mein Shuru Karo
                <ArrowRight size={20} />
              </button>
            </div>
            <p className="text-gray-600 text-xs mt-5">
              Free plan · 14-day money-back on paid plans
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-4 bg-gray-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Frequently Asked <span className="text-blue-400">Questions</span>
            </h2>
            <p className="text-gray-400 text-lg">Everything you need to know about TradeFix AI.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                q: 'What is TradeFix AI?',
                a: 'TradeFix AI is an AI-powered trading performance analyzer built for Indian traders. It helps you identify emotional mistakes, track trading psychology, manage risk, and get personalized AI coaching on every trade — all in one platform.',
              },
              {
                q: 'Is TradeFix AI free to use?',
                a: 'Yes. The free plan lets you log up to 10 trades per month and access 3 AI Coach chats per day. For unlimited trades, advanced analytics, and full AI coaching, upgrade to Pro (₹999/mo) or Elite (₹1999/mo). No credit card required to start.',
              },
              {
                q: 'How does TradeFix AI help Indian traders?',
                a: 'TradeFix AI is designed for NSE, BSE, F&O, intraday, and swing traders. It surfaces behavioral patterns like revenge trading, fear-based early exits, and overtrading, then provides real-time AI coaching so you can trade more consistently and profitably.',
              },
              {
                q: 'What is the difference between Pro and Elite plan?',
                a: 'Pro includes unlimited trades, AI Coach insights, Psychology Tracker, Risk Manager, monthly progress reports, and more. Elite adds weekly reports, import trades, hidden charges analysis, Excel export, 1-on-1 onboarding, and dedicated WhatsApp support.',
              },
              {
                q: 'How do I get started with TradeFix AI?',
                a: 'Getting started takes less than a minute — create a free account with your email and log your first trade. The AI Coach starts analyzing your patterns immediately. No credit card needed for the free plan.',
              },
              {
                q: 'Is there a money-back guarantee?',
                a: 'Yes! All paid plans include a 14-day money-back guarantee. If you are not satisfied within 14 days of purchase, we will refund 100% — no questions asked.',
              },
            ].map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={i}
                  className={`bg-gray-900 border rounded-2xl overflow-hidden transition-all duration-200 ${isOpen ? 'border-blue-700/60' : 'border-gray-800 hover:border-gray-700'}`}
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="text-white font-semibold text-base leading-snug">{item.q}</span>
                    <ChevronDown
                      size={18}
                      className={`text-gray-400 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-400' : ''}`}
                    />
                  </button>
                  <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                    <p className="text-gray-400 text-sm leading-relaxed px-6 pb-5">{item.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* Disclaimer */}
      <div className="px-4 py-6 bg-gray-950 border-t border-gray-800/50">
        <p className="max-w-4xl mx-auto text-center text-gray-600 text-xs leading-relaxed">
          ⚠️ <strong className="text-gray-500">Disclaimer:</strong> TradeFix AI is a data analytics and psychology coaching tool for traders. We do not provide investment advice, trading signals, or financial recommendations. All trading decisions are solely the responsibility of the user.
        </p>
      </div>

      <footer className="bg-gray-950 border-t border-gray-800 px-4 pt-12 pb-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">

            {/* Brand + About */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-green-400 rounded-lg flex items-center justify-center">
                  <TrendingUp size={14} className="text-white" />
                </div>
                <span className="text-lg font-bold text-white">TradeFix AI</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                AI-Powered Performance Mistake Analyzer helping professionals identify emotional patterns and improve performance.
              </p>
              <p className="text-gray-500 text-xs leading-relaxed">
                Operated by <span className="text-gray-400 font-medium">Krish Automation Services</span>
              </p>
            </div>

            {/* About Us */}
            <div>
              <h4 className="text-sm font-semibold text-gray-200 uppercase tracking-wider mb-4">About Us</h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                TradeFix AI is a product of <strong className="text-gray-300">Krish Automation Services</strong>, based in Nagpur, Maharashtra, India. We build AI-driven tools to help professionals become consistently profitable by understanding and eliminating their performance mistakes.
              </p>
            </div>

            {/* Contact Us */}
            <div>
              <h4 className="text-sm font-semibold text-gray-200 uppercase tracking-wider mb-4">Contact Us</h4>
              <ul className="space-y-3">
                <li>
                  <a href="mailto:support@tradefixai.in" className="text-gray-400 text-sm hover:text-blue-400 transition-colors flex items-start gap-2">
                    <span>📧</span>
                    <span>support@tradefixai.in</span>
                  </a>
                </li>
                <li className="text-gray-400 text-sm flex items-start gap-2">
                  <span>🏢</span>
                  <span>Krish Automation Services</span>
                </li>
                <li className="text-gray-400 text-sm flex items-start gap-2">
                  <span>📍</span>
                  <span>Nagpur, Maharashtra, India</span>
                </li>
                <li>
                  <a href="https://wa.me/919373606511" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-sm hover:text-blue-400 transition-colors flex items-start gap-2">
                    <span>💬</span>
                    <span>+91-9373606511 (WhatsApp only)</span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-gray-200 uppercase tracking-wider mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <a href="/terms" className="text-gray-400 text-sm hover:text-blue-400 transition-colors">
                    Terms &amp; Conditions
                  </a>
                </li>
                <li>
                  <a href="/privacy" className="text-gray-400 text-sm hover:text-blue-400 transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/refund" className="text-gray-400 text-sm hover:text-blue-400 transition-colors">
                    Refund Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} TradeFix AI — Krish Automation Services. All rights reserved.
            </p>
            <p className="text-gray-600 text-xs">Made in India 🇮🇳</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
